/**
 * ISM form kayıtları (ADR 0042; plan M8b3): durumlar, eski revizyon işaretleri, Form List'teki
 * kayıt eylemleri, kayıt sayfası, Form History, PDF doldurma, kapak sayfası ve hata metinleri.
 * Gemi uygulaması ve web ofis aynı metinleri kullanır.
 */
export const formsEn = {
  status: {
    draft: 'Draft',
    discarded: 'Discarded',
    pendingStep: 'Pending (step {{step}} of {{steps}})',
    pendingOffice: 'Pending office (step {{step}} of {{steps}})',
    pendingAny: 'Pending',
    returned: 'Returned',
    approved: 'Approved',
    void: 'Void',
    inconsistent: 'Inconsistent',
    amended: 'Amended (rev. {{revision}})',
  },
  flags: {
    superseded_before_submit: 'Filled on a superseded revision',
    withdrawn_before_submit: 'Filled on a withdrawn revision',
    paper_on_superseded: 'Paper copy of a superseded revision',
    paper_on_withdrawn: 'Paper copy of a withdrawn revision',
    not_received: 'Vessel had not received the new revision',
    received_but_superseded: 'Old revision used after the new one arrived',
  },
  fill: {
    title: 'Fill in app · {{record}}',
    hint: 'Fill in the form fields. Scripts in the PDF do not run. Save adds the filled PDF to the record.',
    loading: 'Opening the form…',
    save: 'Save to record',
    cancel: 'Cancel',
    close: 'Close',
    saved: 'The filled PDF was added to the record.',
  },
  fillMethods: {
    office: 'Office (Word or Excel)',
    print_scan: 'Printed and scanned',
    pdf_form: 'Filled in app (PDF)',
  },
  fileSources: {
    office: 'Office file',
    scan: 'Scan',
    photo: 'Photo',
    pdf_form: 'Filled PDF',
    office_pdf: 'PDF copy of the Office file',
  },
  delivery: {
    ship: 'Saved on board',
    office: 'Received by the office',
  },
  dialog: {
    back: 'Back',
    dismiss: 'Dismiss',
    reason: 'Reason',
  },
  actions: {
    fillIn: 'Fill in',
    fillInApp: 'Fill in app',
    newRecord: 'New record',
    printTitle: 'Print for a new record',
    printBody:
      'Print Form opens a new record for {{form}} and prints the form with the record number in the footer, so the scanned paper is linked to the record. Each Print Form uses a record number. To print a blank copy without opening a record, use the printer button on the preview.',
    printConfirm: 'Open record and print',
    noApp:
      'No application on this computer opens this form. Record {{record}} was discarded. Fill it in on paper instead: Print, then upload the signed scan or photo.',
    noAppDiscard: 'Fill in: no application on this computer opens the file.',
    // Kayıt yolları kapalıyken görünür not (ADR 0055 §1)
    notice: {
      draft:
        'Rev. {{revision}} is a draft. Submit it for approval; records can be opened after the master approves and publishes it.',
      pending_approval:
        'Rev. {{revision}} is waiting for approval. Records can be opened after it is approved and published.',
      approved:
        'Rev. {{revision}} is approved but not published. Publish it to put it in force; then records can be opened.',
      scheduled:
        'Rev. {{revision}} comes into force on {{date}}. Records can be opened from that day.',
      none: 'No revision of this form is in force, so records cannot be opened.',
    },
  },
  record: {
    pageTitle: 'Form record',
    heading: 'Record {{record}}',
    formNo: 'Form {{no}}',
    loading: 'Loading the record…',
    retry: 'Try again',
    title: 'Title',
    note: 'Note',
    saveDraft: 'Save Draft',
    submit: 'Submit',
    saved: 'The draft was saved.',
    submitted: 'The record was submitted for approval.',
    approved: 'The record was approved.',
    returned: 'The record was returned.',
    amended: 'The amendment was submitted for approval.',
    openInOffice: 'Open in Office',
    openedInOffice:
      'The form opened in Office. Fill it in, save and close it, then choose Attach to record.',
    // Felixmate Office Pack (ADR 0070 §8): kapanınca kendiliğinden eklenir
    openedInOfficePack:
      'The form opened in Felixmate Office Pack. Fill it in, save and close it: the form and a PDF copy are added to the record automatically.',
    autoAttached:
      'The form saved in Felixmate Office Pack was added to the record with a PDF copy.',
    autoAttachedWithoutPdf: 'The saved form was added to the record. A PDF copy could not be made.',
    autoNotSaved: 'The form was closed without saving; nothing was added to the record.',
    autoNotOpened: 'Felixmate Office Pack did not open the form. Choose Open in Office again.',
    autoFailed: 'The saved form could not be added to the record. Choose Attach to record.',
    attach: 'Attach to record',
    attachAnyway: 'Attach anyway',
    attached: 'The saved file was attached to the record.',
    stillOpenTitle: 'The document is still open',
    discard: 'Discard draft',
    amend: 'Amend',
    resubmit: 'Resubmit unchanged',
    void: 'Void record',
    download: 'Download file',
    supersededTitle: 'A newer revision of this form is in force',
    supersededText:
      'This draft was opened with {{revision}}, which is no longer in force. You may still submit it; the record will be flagged. Or open a new record with the current revision.',
    reopenCurrent: 'Open with the current revision',
    returnedTitle: 'Returned for correction',
    returnedText: 'Returned by {{by}} on {{at}}.',
    returnedOffice: 'Returned by the office ({{by}}) on {{at}}.',
    returnedReason: 'Reason: {{reason}}',
    returnedHint: 'Amend the record, or resubmit it unchanged with a note.',
  },
  info: {
    title: 'Record Information',
    recordNo: 'Record No',
    vessel: 'Vessel',
    form: 'Form',
    revision: 'Form revision',
    sources: {
      company: 'Company document',
      vessel: 'Vessel document',
    },
    fillMethod: 'Filled by',
    recordDay: 'Record date',
    enteredBy: 'Entered by',
    createdAt: 'Opened',
    status: 'Status',
    awaiting: 'Awaiting',
    awaitingValue: '{{role}} since {{since}}',
    delivery: 'Delivery',
    flags: 'Revision flags',
    paperReason: 'Reason for the paper copy',
  },
  guide: {
    title: 'How to complete this record',
    done: 'done',
    step1: 'Get the form',
    step1Text: {
      office:
        'Use Open in Office under Actions to fill the form on this computer, or download the template and fill it in elsewhere.',
      pdf_form:
        'Use Fill in app under Actions to fill the PDF here, or download the template and fill it in elsewhere.',
      print_scan: 'Use the printed form, or download the template to print another copy.',
    },
    step2: 'Fill it in',
    step2Text: 'Complete every field. On paper, write clearly and sign where required.',
    step3: 'Upload the completed file',
    step3Text:
      'Upload the filled-in file, or scan or photograph the signed paper and upload it. The files appear in Record Files below.',
    step4: 'Submit',
    // ISM formunda gönderme ve onay açık adımla; şirket "her yerde iste" derse parola (ADR 0056)
    step4Text:
      'Check the files, then choose Submit at the bottom of the page and confirm. The record goes for approval.',
    step4TextPassword:
      'Check the files, then choose Submit at the bottom of the page. Your password signs the record and sends it for approval.',
    downloadTemplate: 'Download template',
    upload: 'Upload filled file',
    accepted:
      'Accepted: PDF, Word (.docx), Excel (.xlsx), JPG, PNG. Up to 50 MB per file and 20 files per record. Files with macros are not accepted.',
  },
  // Gemide sade form sayfası ve kayıt akışı (sahip kararı 2026-10-17; ADR 0074)
  page: {
    title: 'How to fill in',
    office: {
      step1: 'Fill in',
      step1Text: 'Opens the form in Felixmate Office Pack.',
      step2: 'Save and close',
      step2Text: 'The filled file is attached to a new record automatically.',
      step3: 'Submit',
      step3Text: 'Check the record and submit it for approval.',
    },
    paper: {
      step1: 'Print',
      step1Text: 'A record opens; its number is printed in the footer.',
      step2: 'Sign',
      step2Text: 'Fill in the paper and sign it.',
      step3: 'Upload',
      step3Text: 'Scan or photograph it, upload it to the record and submit.',
    },
    paperRecordHint:
      'Already printed? The record is listed under the form in the tree: open it and upload the scan there.',
    onPaper: 'Filling it on paper?',
    onPaperText: 'Print it with a record number, then upload the signed scan or photo.',
    print: 'Print',
    printForRecord: 'Print for a record',
    upload: 'Upload scan / photo',
    uploadSigned: 'Upload signed form',
    fillable: 'This PDF has fields: you can also fill it in on screen.',
    fillInApp: 'Fill in on screen',
  },
  howTo: {
    title: 'Complete this record',
    office: {
      step1: 'Fill in',
      step1Text: 'Opens the form in Office.',
      reopen: 'Open again',
      step2: 'Save and close Office',
      step2Text: 'The filled file is attached here automatically.',
      attach: 'Not attached? Attach the saved file',
    },
    print_scan: {
      step1: 'Fill in and sign',
      step1Text: 'Fill in the printed form and sign it.',
      step2: 'Upload scan / photo',
      step2Text: 'Scan or photograph every page and upload it.',
    },
    pdf_form: {
      step1: 'Fill in app',
      step1Text: 'Fill in the PDF on screen and save it.',
      step2: 'Check the file',
      step2Text: 'The filled PDF is attached to this record.',
    },
    step3: 'Submit',
    status: {
      waitingOffice: 'No filled file yet. Choose Fill in.',
      attachedOffice: 'Filled file attached from Office ✓',
      none: 'No file attached yet.',
      files_one: '{{count}} file attached ✓',
      files_other: '{{count}} files attached ✓',
    },
    officeOpen:
      'The form is open in Office. Fill it in, save and close Office — the file is attached here automatically.',
    front: 'Bring Office to front',
    closeOfficeFirst: 'Close Office first: the filled file is attached automatically.',
    onPaper: 'Filled on paper?',
    upload: 'Upload scan / photo',
    confirmUploadTitle: 'Upload a paper scan?',
    confirmUploadBody: 'A filled file is already attached from Office. Upload a paper scan anyway?',
    confirmUpload: 'Upload anyway',
  },
  files: {
    title: 'Record Files',
    none: 'No files.',
    // Dosya başına sonuç ve ilerleme (ADR 0055 §3, §4)
    rejected: 'Not added: {{list}} The other files were added.',
    uploadingProgress: 'Uploading {{name}} ({{index}} of {{count}}) – {{percent}}%',
    emptyDraft: 'No files yet. Follow the steps above: the filled file appears here.',
    draftOnVessel: 'Draft (on vessel): the files stay on board until the record is submitted.',
    notInOffice: 'This file has not reached the office yet.',
    preview: 'Preview',
    previewName: 'Preview {{name}}',
    up: 'Move {{name}} up',
    down: 'Move {{name}} down',
    remove: 'Remove {{name}}',
    addScan: 'Add scan',
    addPhoto: 'Add photo',
    ship: {
      pending: 'Waiting to upload',
      uploading: 'Uploading',
      verified: 'Uploaded',
      failed: 'Upload failed',
    },
    office: {
      verified: 'Available',
      uploading: 'Uploading',
      uploadingPercent: 'Uploading {{percent}}%',
      window: 'Waiting for upload window',
      on_vessel: 'On vessel',
      rejected: 'Rejected',
    },
  },
  revisions: {
    title: 'Revisions and approvals',
    none: 'Not submitted yet.',
    revision: 'Record revision {{no}}',
    first: 'Submitted',
    amendment: 'Amended',
    hash: 'Hash {{hash}}',
    reason: 'Reason:',
    note: 'Note:',
    approvals: 'Approvals',
    decisions: {
      approved: 'Step {{step}} approved',
      returned: 'Step {{step}} returned',
    },
    officeStep: 'office step',
    roleUnverified: 'Role on that day not verified',
    awaiting: 'NOT APPROVED – awaiting {{role}} since {{since}}',
    overdue: 'Overdue',
    void: 'VOID – {{by}}, {{at}}: {{reason}}',
  },
  decision: {
    title: 'Approval',
    // Kararın yeri (ADR 0073): ISM formu gemide kapanır, ofis yalnız görür
    onBoard: 'Approved on board by the {{role}}. The office sees the record only.',
    officeStep: 'Waiting for {{role}} to decide in the office.',
    subject: 'ISM form record {{record}}',
    lateReason: 'Reason (the step is overdue: explain the late decision)',
  },
  submit: {
    title: 'Submit {{record}}',
    body_one:
      'Submitting signs record {{record}} with its {{count}} file and sends it for approval. Changes after this are made with Amend and keep this revision visible.',
    body_other:
      'Submitting signs record {{record}} with its {{count}} files and sends it for approval. Changes after this are made with Amend and keep this revision visible.',
  },
  resubmit: {
    title: 'Resubmit unchanged',
    body: 'Record {{record}} is sent for approval again with the same files and revision.',
    reason: 'Reason for resubmitting unchanged',
  },
  void: {
    title: 'Void {{record}}',
    body: 'A voided record is not deleted: its revisions and files stay visible and it is marked VOID. Voiding cannot be undone; open a new record if needed.',
    reason: 'Reason for voiding',
    note: 'You are voiding this record. Your name, the time and the reason are recorded with it.',
  },
  discard: {
    title: 'Discard draft {{record}}',
    body: 'The draft was never submitted. It stays listed as Discarded with your reason; the record number is not reused.',
  },
  newRecord: {
    title: 'New record',
    intro:
      'Record a paper form for {{form}} with a scan or a photo. Opening the record reserves a record number; add the pages on the record page.',
    paperOf: 'The paper was printed from',
    current: 'The revision in force ({{revision}})',
    noCurrent: 'No revision is in force',
    paper: 'An earlier revision (paper copy)',
    revision: 'Revision of the paper copy',
    paperReason: 'Why an earlier revision was used',
    paperHint: 'The record is flagged as a paper copy of an earlier revision.',
    titleHint: 'Optional, e.g. the drill or the date of the event.',
    number: 'A record number is reserved when the record opens.',
    create: 'Open record',
  },
  print: {
    record: 'Record {{record}}',
    void: 'VOID',
    amended: 'AMENDED – record revision {{revision}} of {{revisions}}',
    notApproved: 'NOT APPROVED',
    enteredBy: 'Entered by {{name}}',
    printed: 'Printed {{at}} by {{by}}',
    hash: 'Hash {{hash}}',
    page: 'Page {{page}} / {{pages}}',
  },
  history: {
    description: 'Filled ISM forms of this vessel with their approvals.',
    descriptionOffice:
      'Filled ISM forms of the fleet with their approvals. Records are entered on board in the ship application and arrive with synchronisation; the office views them and decides office steps.',
    filters: 'Filter form records',
    status: 'Status',
    category: 'Category',
    form: 'Form',
    vessel: 'Vessel',
    from: 'From',
    to: 'To',
    all: 'All',
    allVessels: 'All vessels',
    awaitingMe: 'Awaiting my approval',
    upcomingMe: 'Upcoming for me (not my turn yet)',
    amended: 'Amended',
    flagged: 'Filled on an old revision',
    overdue: 'Overdue only',
    clear: 'Clear filters',
    loading: 'Loading form records…',
    loadError: 'The records could not be loaded.',
    empty: 'No form records yet',
    emptyHint: 'Records appear here when a form is filled from Form List.',
    emptyHintOffice:
      'Records appear here when a vessel submits a filled form. Forms are filled in on board in the ship application.',
    noMatch: 'No records match the filters',
    caption: 'Form records',
    columns: {
      recordNo: 'Record No',
      recordDay: 'Date',
      vessel: 'Vessel',
      form: 'Form',
      title: 'Title',
      status: 'Status',
      awaitingSince: 'Awaiting since',
    },
    sortBy: 'Sort by {{column}}',
    showing: 'Showing {{first}}–{{last}} of {{total}} records',
    pages: 'Pages of form records',
    ofForm: 'Records of this form',
    noneForForm: 'No records of this form yet.',
    viewAll_one: 'View the record in Form History',
    viewAll_other: 'View all {{count}} records in Form History',
  },
  cover: {
    title: 'ISM form record',
    recordNo: 'Record No',
    vessel: 'Vessel',
    form: 'Form',
    revision: 'Form revision',
    fillMethod: 'Filled by',
    recordDay: 'Record date',
    title_: 'Title',
    status: 'Status',
    enteredBy: 'Entered by',
    opened: 'Opened',
    printed: 'Printed',
    revisions: 'Record revisions',
    rev: 'Rev',
    at: 'Time',
    by: 'By',
    reason: 'Reason',
    hash: 'Hash',
    firstSubmission: 'First submission',
    approvals: 'Approvals',
    noApprovals: 'No decisions yet.',
    matrix: 'Matrix v{{version}}',
    step: 'Step',
    decision: 'Decision',
    approver: 'Approver and role',
    shipTime: 'Ship time',
    utc: 'UTC',
    files: 'Files',
    file: 'File',
    source: 'Source',
    sha256: 'SHA-256 (12)',
    size: 'Size',
    flags: 'Revision flags',
    download: 'Download record PDF',
    print: 'Print record',
    // İmza bloğu (ADR 0069)
    signatures: 'Signatures',
    signatureStep: 'Step {{step}} · {{role}}',
    signatureTime: '{{utc}} (ship time {{offset}})',
    signatureHash: 'Hash {{hash}}',
    unsigned: 'Awaiting decision',
  },
  approvals: {
    title: 'Pending approvals',
    viewAll: 'View all',
    pending_one: '{{count}} awaiting your decision',
    pending_other: '{{count}} awaiting your decision',
    overdue_one: '{{count}} overdue',
    overdue_other: '{{count}} overdue',
    none: 'Nothing awaits your decision.',
    step: 'Step {{step}} of {{steps}}',
    bellPending: '{{record}} awaits your approval',
    bellOverdue: '{{record}} is overdue for your approval',
    bellNotice: 'Your draft {{record}} uses a revision no longer in force',
    bellReturned: '{{record}} returned to you',
    // Sırası gelmemiş kayıtlar (ADR 0069)
    upcoming: 'Upcoming',
  },
  returned: {
    title: 'Returned to you',
    by: '{{by}}: {{reason}}',
    noReason: 'No reason given',
  },
  steps: {
    title: 'Approval steps',
    step: 'Step {{step}}',
    awaiting: 'Awaiting decision',
    notYet: 'Not yet',
    waitingFor: 'Waiting for {{role}}',
    // Form zinciri (ADR 0071): adım türleri ve hazırlayanın gönderim imzası
    submitted: 'Submitted',
    kinds: {
      prepare: 'Prepared by',
      review: 'Review',
      master: 'Master approval',
      office: 'Office approval',
    },
  },
  amend: {
    title: 'Amend {{record}} (revision {{revision}})',
    intro:
      'An amendment is a new record revision with a reason. The previous revision and its files stay visible, and the record goes to approval again from the first step.',
    reason: 'Reason for the change',
    reasonHint: '5 to 2000 characters; it is printed on the record.',
    submit: 'Submit amendment',
    confirmTitle: 'Submit the amendment',
    confirmBody: 'The amendment of {{record}} is signed in your name and sent for approval.',
    // Verilmiş ön onaylar düşer (sahip kararı 2026-10-17; ADR 0069)
    dropsApprovals_one:
      'The approval already given ({{count}}) will be cleared and the record will start again from step 1.',
    dropsApprovals_other:
      'The approvals already given ({{count}}) will be cleared and the record will start again from step 1.',
  },
  errors: {
    GENERIC: 'The action could not be completed. Try again.',
    FORBIDDEN: 'You do not have permission for this action.',
    NOT_FOUND: 'The record was not found or you cannot see it.',
    UNAUTHENTICATED: 'Your session has ended. Sign in again.',
    VALIDATION_FAILED: 'Some values are not valid.',
    INVALID_CREDENTIALS: 'The password is not correct.',
    INVALID_PASSWORD: 'The password is not correct.',
    REAUTHENTICATION_REQUIRED: 'Enter your password to confirm.',
    TOO_MANY_ATTEMPTS: 'Too many wrong passwords. Wait a few minutes and try again.',
    ROW_VERSION_CONFLICT: 'Someone else changed this draft. Reload and try again.',
    REVISION_NOT_CURRENT: 'This revision of the form is not in force.',
    REVISION_NOT_PUBLISHED:
      'This revision was never in force on board; it cannot be used for a paper copy.',
    REVISION_NOT_FOUND: 'The form revision was not found.',
    REVISION_MISMATCH: 'The revision does not belong to this form.',
    NOT_A_FORM: 'Records can only be opened on forms.',
    VESSEL_MISMATCH: 'This form belongs to another vessel.',
    DOCUMENT_NOT_VISIBLE: 'This form is not available on this vessel.',
    FILL_METHOD_UNSUPPORTED: 'This way of filling is not available for this form.',
    RECORD_BINDING_LOCKED: 'The record stays bound to the revision it was opened with.',
    FILE_NOT_ON_BOARD: 'The form file has not arrived on board yet.',
    FILE_TOO_LARGE: 'The file is too large (50 MB per file, 200 MB per record revision).',
    TOO_MANY_FILES: 'A record revision can have at most 20 files.',
    NO_FILES: 'Add at least one file before submitting.',
    EMPTY_FILE: 'The file is empty.',
    DUPLICATE_FILE: 'This file is already in the record.',
    UNSUPPORTED_FILE_TYPE:
      'This file type is not accepted (DOCX, XLSX, PDF, JPG or PNG; files with macros are refused).',
    FILE_TYPE_MISMATCH: 'The file content does not match its name.',
    INVALID_FILE: 'The file could not be read.',
    SHIP_FILE_MISSING: 'The file is no longer in the ship store. Add it again.',
    HASH_MISMATCH: 'The file changed while it was being sent. Add it again.',
    FILES_UNAVAILABLE: 'The ship file store is not available.',
    DISK_CRITICAL: 'The ship server disk is almost full.',
    RECORD_SUBMITTED: 'The record has been submitted; use Amend to change it.',
    RECORD_VOID: 'The record is void.',
    RECORD_DISCARDED: 'The draft was discarded.',
    RECORD_NOT_SUBMITTED: 'The record has not been submitted yet.',
    ALREADY_VOIDED: 'The record is already void.',
    ALREADY_SUBMITTED: 'The record has already been submitted.',
    INVALID_RECORD_STATE:
      'The state of this record cannot be determined. It is shown as it is; no action is possible.',
    NOT_RETURNED: 'Only a returned record can be resubmitted without changes.',
    NOT_PENDING: 'The record is no longer awaiting a decision.',
    NO_CHANGE: 'Nothing has changed compared with the previous revision.',
    REASON_REQUIRED: 'Enter a reason (at least 5 characters).',
    REASON_TOO_LONG: 'The reason is too long (2000 characters at most).',
    DISCARD_NOT_ALLOWED: 'You may not discard this draft.',
    CONTENT_HASH_MISMATCH: 'The record content changed. Reload and try again.',
    RETURN_REASON_REQUIRED: 'Enter the reason for returning the record.',
    LATE_REASON_REQUIRED: 'The step is overdue: explain the late decision.',
    STALE_DECISION: 'The record changed while you were looking at it. Reload and decide again.',
    CONTENT_CHANGED: 'The record content changed. Reload and decide again.',
    NO_ELIGIBLE_APPROVER:
      'No one on board can approve this record. Ask the office to check the approval matrix.',
    NOT_APPROVER: 'Your role does not approve this step.',
    NOT_A_PREPARER:
      'This form is prepared by the ranks set in its approval chain. Ask one of them to submit it.',
    SELF_APPROVAL_FORBIDDEN: 'You cannot approve a record you entered or submitted.',
    SAME_APPROVER_FORBIDDEN: 'You already approved an earlier step of this record.',
    OFFICE_STEP: 'This step is decided in the office.',
    NOT_OFFICE_STEP: 'This step is decided on board.',
    FILE_NOT_UPLOADED: 'The record files have not reached the office yet.',
    APPROVAL_RULE_MISSING: 'The approval matrix has no rule for ISM forms.',
    OPEN_FORM_NOT_OFFICE:
      'This form is a PDF or an image: use Print Form, or Fill in app for a fillable PDF.',
    NO_ASSOCIATED_APP:
      'No application on this computer opens this file type. Use Print Form or Fill in app instead.',
    FILE_STILL_OPEN: 'The document still seems to be open. Save and close it first.',
    NO_WORKING_COPY: 'Open the form with Fill in first, fill it in and save it.',
    UNCHANGED_TEMPLATE: 'The file looks unfilled: it is identical to the blank form.',
    PDF_FAILED: 'The PDF copy of the form could not be made; the form itself was attached.',
    OFFICE_PACK_MISSING: 'Felixmate Office Pack is not installed on this computer.',
    LOW_RESOLUTION: 'The image is below 300 dpi on A4; handwriting may be hard to read.',
    OPEN_FAILED: 'The file could not be opened on this computer.',
    READ_FAILED: 'The file could not be read from this computer.',
    SHIP_UNREACHABLE: 'The ship server cannot be reached.',
    PDF_HAS_SCRIPTS: 'This PDF contains scripts and cannot be filled in the app.',
    PDF_IS_XFA: 'This PDF uses XFA forms and cannot be filled in the app.',
    PDF_NO_FIELDS: 'This PDF has no form fields.',
    PDF_FORM_CHECK_FAILED:
      'The PDF could not be opened on this computer to check its form fields. Select the form again, or use Print Form or New record.',
    PREVIEW_NOT_READY: 'The preview is still loading. Try again in a moment.',
    POPUP_BLOCKED: 'The browser blocked the new tab. Allow pop-ups for this site.',
    NETWORK: 'The server cannot be reached.',
  },
};

export const formsTr: typeof formsEn = {
  status: {
    draft: 'Taslak',
    discarded: 'Vazgeçildi',
    pendingStep: 'Onay bekliyor ({{step}}. adım / {{steps}})',
    pendingOffice: 'Ofis onayı bekliyor ({{step}}. adım / {{steps}})',
    pendingAny: 'Onay bekliyor',
    returned: 'İade edildi',
    approved: 'Onaylandı',
    void: 'İptal',
    inconsistent: 'Tutarsız',
    amended: 'Değiştirildi (rev. {{revision}})',
  },
  flags: {
    superseded_before_submit: 'Yürürlükten kalkmış revizyonla dolduruldu',
    withdrawn_before_submit: 'Geri çekilmiş revizyonla dolduruldu',
    paper_on_superseded: 'Yürürlükten kalkmış revizyonun basılı kopyası',
    paper_on_withdrawn: 'Geri çekilmiş revizyonun basılı kopyası',
    not_received: 'Gemi yeni revizyonu henüz almamıştı',
    received_but_superseded: 'Yeni revizyon geldikten sonra eskisi kullanıldı',
  },
  fill: {
    title: 'Uygulamada doldur · {{record}}',
    hint: 'Form alanlarını doldurun. PDF içindeki betikler çalışmaz. Kaydet, doldurulan PDF’i kayda ekler.',
    loading: 'Form açılıyor…',
    save: 'Kayda kaydet',
    cancel: 'Vazgeç',
    close: 'Kapat',
    saved: 'Doldurulan PDF kayda eklendi.',
  },
  fillMethods: {
    office: 'Office (Word ya da Excel)',
    print_scan: 'Basılıp tarandı',
    pdf_form: 'Uygulamada dolduruldu (PDF)',
  },
  fileSources: {
    office: 'Office dosyası',
    scan: 'Tarama',
    photo: 'Fotoğraf',
    pdf_form: 'Doldurulan PDF',
    office_pdf: 'Office dosyasının PDF kopyası',
  },
  delivery: {
    ship: 'Gemide kaydedildi',
    office: 'Ofise ulaştı',
  },
  dialog: {
    back: 'Geri',
    dismiss: 'Kapat',
    reason: 'Gerekçe',
  },
  actions: {
    fillIn: 'Formu doldur',
    fillInApp: 'Uygulamada doldur',
    newRecord: 'Yeni kayıt',
    printTitle: 'Yeni kayıt için yazdır',
    printBody:
      'Print Form, {{form}} için yeni bir kayıt açar ve formu altlığında kayıt numarasıyla basar; böylece taranan kâğıt kayda bağlanır. Her Print Form bir kayıt numarası kullanır. Kayıt açmadan boş kopya basmak için önizlemedeki yazıcı düğmesini kullanın.',
    printConfirm: 'Kayıt aç ve yazdır',
    noApp:
      'Bu bilgisayarda bu formu açan uygulama yok. {{record}} kaydından vazgeçildi. Bunun yerine kâğıtta doldurun: Yazdır, sonra imzalı taramayı ya da fotoğrafı yükleyin.',
    noAppDiscard: 'Formu doldur: bu bilgisayarda dosyayı açan uygulama yok.',
    notice: {
      draft:
        'Rev. {{revision}} taslak. Onaya gönderin; kaptan onaylayıp yayımladıktan sonra kayıt açılabilir.',
      pending_approval:
        'Rev. {{revision}} onay bekliyor. Onaylanıp yayımlandıktan sonra kayıt açılabilir.',
      approved:
        'Rev. {{revision}} onaylandı ama yayımlanmadı. Yürürlüğe koymak için yayımlayın; sonra kayıt açılabilir.',
      scheduled:
        'Rev. {{revision}} {{date}} günü yürürlüğe girer. Kayıt o günden itibaren açılabilir.',
      none: 'Bu formun yürürlükte revizyonu yok; kayıt açılamaz.',
    },
  },
  record: {
    pageTitle: 'Form kaydı',
    heading: 'Kayıt {{record}}',
    formNo: 'Form {{no}}',
    loading: 'Kayıt yükleniyor…',
    retry: 'Yeniden dene',
    title: 'Başlık',
    note: 'Not',
    saveDraft: 'Taslağı kaydet',
    submit: 'Gönder',
    saved: 'Taslak kaydedildi.',
    submitted: 'Kayıt onaya gönderildi.',
    approved: 'Kayıt onaylandı.',
    returned: 'Kayıt iade edildi.',
    amended: 'Değişiklik onaya gönderildi.',
    openInOffice: 'Office’te aç',
    openedInOffice: 'Form Office’te açıldı. Doldurun, kaydedip kapatın, sonra Kayda ekle’yi seçin.',
    openedInOfficePack:
      'Form Felixmate Office Pack’te açıldı. Doldurun, kaydedip kapatın: form ve PDF kopyası kayda kendiliğinden eklenir.',
    autoAttached: 'Felixmate Office Pack’te kaydedilen form PDF kopyasıyla kayda eklendi.',
    autoAttachedWithoutPdf: 'Kaydedilen form kayda eklendi. PDF kopyası üretilemedi.',
    autoNotSaved: 'Form kaydedilmeden kapandı; kayda bir şey eklenmedi.',
    autoNotOpened: 'Felixmate Office Pack formu açmadı. Office’te aç’ı yeniden seçin.',
    autoFailed: 'Kaydedilen form kayda eklenemedi. Kayda ekle’yi seçin.',
    attach: 'Kayda ekle',
    attachAnyway: 'Yine de ekle',
    attached: 'Kaydedilen dosya kayda eklendi.',
    stillOpenTitle: 'Belge hâlâ açık',
    discard: 'Taslaktan vazgeç',
    amend: 'Değiştir (Amend)',
    resubmit: 'Değişmeden yeniden gönder',
    void: 'Kaydı iptal et',
    download: 'Dosyayı indir',
    supersededTitle: 'Bu formun daha yeni bir revizyonu yürürlükte',
    supersededText:
      'Bu taslak artık yürürlükte olmayan {{revision}} ile açıldı. Yine de gönderebilirsiniz; kayıt işaretlenir. Ya da yürürlükteki revizyonla yeni kayıt açın.',
    reopenCurrent: 'Yürürlükteki revizyonla aç',
    returnedTitle: 'Düzeltme için iade edildi',
    returnedText: '{{by}} {{at}} tarihinde iade etti.',
    returnedOffice: 'Ofis ({{by}}) {{at}} tarihinde iade etti.',
    returnedReason: 'Gerekçe: {{reason}}',
    returnedHint: 'Kaydı değiştirin ya da açıklamayla değişmeden yeniden gönderin.',
  },
  info: {
    title: 'Kayıt Bilgileri',
    recordNo: 'Kayıt No',
    vessel: 'Gemi',
    form: 'Form',
    revision: 'Form revizyonu',
    sources: {
      company: 'Şirket belgesi',
      vessel: 'Gemi belgesi',
    },
    fillMethod: 'Doldurma yolu',
    recordDay: 'Kayıt tarihi',
    enteredBy: 'Giren',
    createdAt: 'Açılış',
    status: 'Durum',
    awaiting: 'Bekleyen',
    awaitingValue: '{{role}}, {{since}} tarihinden beri',
    delivery: 'Teslim',
    flags: 'Revizyon işaretleri',
    paperReason: 'Basılı kopyanın gerekçesi',
  },
  guide: {
    title: 'Bu kayıt nasıl tamamlanır',
    done: 'tamam',
    step1: 'Formu alın',
    step1Text: {
      office:
        'Formu bu bilgisayarda doldurmak için İşlemler’deki Office’te aç’ı kullanın ya da şablonu indirip başka yerde doldurun.',
      pdf_form:
        'PDF’i burada doldurmak için İşlemler’deki Uygulamada doldur’u kullanın ya da şablonu indirip başka yerde doldurun.',
      print_scan: 'Basılı formu kullanın ya da bir kopya daha basmak için şablonu indirin.',
    },
    step2: 'Doldurun',
    step2Text: 'Bütün alanları doldurun. Kâğıtta okunaklı yazın ve gereken yerleri imzalayın.',
    step3: 'Doldurulmuş dosyayı yükleyin',
    step3Text:
      'Doldurulmuş dosyayı yükleyin ya da imzalı kâğıdı tarayıp veya fotoğrafını çekip yükleyin. Dosyalar aşağıda Kayıt Dosyaları’nda görünür.',
    step4: 'Gönderin',
    step4Text:
      'Dosyaları kontrol edin, sonra sayfanın altındaki Gönder’i seçip onaylayın. Kayıt onaya gider.',
    step4TextPassword:
      'Dosyaları kontrol edin, sonra sayfanın altındaki Gönder’i seçin. Parolanız kaydı imzalar ve onaya gönderir.',
    downloadTemplate: 'Şablonu indir',
    upload: 'Doldurulmuş dosyayı yükle',
    accepted:
      'Kabul edilenler: PDF, Word (.docx), Excel (.xlsx), JPG, PNG. Dosya başına en çok 50 MB, kayıt başına 20 dosya. Makrolu dosya kabul edilmez.',
  },
  page: {
    title: 'Nasıl doldurulur',
    office: {
      step1: 'Formu doldur',
      step1Text: 'Form Felixmate Office Pack’te açılır.',
      step2: 'Kaydet ve kapat',
      step2Text: 'Doldurulan dosya yeni bir kayda kendiliğinden eklenir.',
      step3: 'Gönder',
      step3Text: 'Kaydı kontrol edip onaya gönderin.',
    },
    paper: {
      step1: 'Yazdır',
      step1Text: 'Bir kayıt açılır; numarası altlığa basılır.',
      step2: 'İmzala',
      step2Text: 'Kâğıdı doldurup imzalayın.',
      step3: 'Yükle',
      step3Text: 'Tarayın ya da fotoğrafını çekin, kayda yükleyip gönderin.',
    },
    paperRecordHint:
      'Önceden bastınız mı? Kayıt ağaçta formun altında listelenir: onu açıp taramayı orada yükleyin.',
    onPaper: 'Kâğıtta mı dolduruyorsunuz?',
    onPaperText: 'Kayıt numarasıyla basın, sonra imzalı taramayı ya da fotoğrafı yükleyin.',
    print: 'Yazdır',
    printForRecord: 'Kayıt için yazdır',
    upload: 'Tarama / fotoğraf yükle',
    uploadSigned: 'İmzalı formu yükle',
    fillable: 'Bu PDF’in alanları var: ekranda da doldurabilirsiniz.',
    fillInApp: 'Ekranda doldur',
  },
  howTo: {
    title: 'Bu kaydı tamamlayın',
    office: {
      step1: 'Formu doldur',
      step1Text: 'Form Office’te açılır.',
      reopen: 'Yeniden aç',
      step2: 'Kaydet ve Office’i kapat',
      step2Text: 'Doldurulan dosya buraya kendiliğinden eklenir.',
      attach: 'Eklenmedi mi? Kaydedilen dosyayı ekleyin',
    },
    print_scan: {
      step1: 'Doldur ve imzala',
      step1Text: 'Basılı formu doldurup imzalayın.',
      step2: 'Tarama / fotoğraf yükle',
      step2Text: 'Her sayfayı tarayın ya da fotoğrafını çekip yükleyin.',
    },
    pdf_form: {
      step1: 'Uygulamada doldur',
      step1Text: 'PDF’i ekranda doldurup kaydedin.',
      step2: 'Dosyayı kontrol et',
      step2Text: 'Doldurulan PDF bu kayda eklenir.',
    },
    step3: 'Gönder',
    status: {
      waitingOffice: 'Henüz doldurulmuş dosya yok. Formu doldur’u seçin.',
      attachedOffice: 'Doldurulan dosya Office’ten eklendi ✓',
      none: 'Henüz dosya eklenmedi.',
      files_one: '{{count}} dosya eklendi ✓',
      files_other: '{{count}} dosya eklendi ✓',
    },
    officeOpen:
      'Form Office’te açık. Doldurun, kaydedip Office’i kapatın — dosya buraya kendiliğinden eklenir.',
    front: 'Office’i öne getir',
    closeOfficeFirst: 'Önce Office’i kapatın: doldurulan dosya kendiliğinden eklenir.',
    onPaper: 'Kâğıtta mı doldurdunuz?',
    upload: 'Tarama / fotoğraf yükle',
    confirmUploadTitle: 'Kâğıt taraması yüklensin mi?',
    confirmUploadBody:
      'Office’ten doldurulmuş bir dosya zaten ekli. Yine de kâğıt taraması yüklensin mi?',
    confirmUpload: 'Yine de yükle',
  },
  files: {
    title: 'Kayıt Dosyaları',
    none: 'Dosya yok.',
    rejected: 'Eklenmedi: {{list}} Diğer dosyalar eklendi.',
    uploadingProgress: '{{name}} yükleniyor ({{index}} / {{count}}) – %{{percent}}',
    emptyDraft: 'Henüz dosya yok. Yukarıdaki adımları izleyin: doldurulan dosya burada görünür.',
    draftOnVessel: 'Taslak (gemide): dosyalar kayıt gönderilene kadar gemide kalır.',
    notInOffice: 'Bu dosya henüz ofise ulaşmadı.',
    preview: 'Önizle',
    previewName: '{{name}} önizle',
    up: '{{name}} yukarı taşı',
    down: '{{name}} aşağı taşı',
    remove: '{{name}} çıkar',
    addScan: 'Tarama ekle',
    addPhoto: 'Fotoğraf ekle',
    ship: {
      pending: 'Yüklenmeyi bekliyor',
      uploading: 'Yükleniyor',
      verified: 'Yüklendi',
      failed: 'Yükleme başarısız',
    },
    office: {
      verified: 'Mevcut',
      uploading: 'Yükleniyor',
      uploadingPercent: 'Yükleniyor %{{percent}}',
      window: 'Yükleme penceresi bekleniyor',
      on_vessel: 'Gemide',
      rejected: 'Reddedildi',
    },
  },
  revisions: {
    title: 'Revizyonlar ve onaylar',
    none: 'Henüz gönderilmedi.',
    revision: 'Kayıt revizyonu {{no}}',
    first: 'Gönderildi',
    amendment: 'Değiştirildi',
    hash: 'Hash {{hash}}',
    reason: 'Gerekçe:',
    note: 'Not:',
    approvals: 'Onaylar',
    decisions: {
      approved: '{{step}}. adım onaylandı',
      returned: '{{step}}. adım iade edildi',
    },
    officeStep: 'ofis adımı',
    roleUnverified: 'O günkü rol doğrulanamadı',
    awaiting: 'ONAYLANMADI – {{since}} tarihinden beri {{role}} bekleniyor',
    overdue: 'Süresi geçti',
    void: 'İPTAL – {{by}}, {{at}}: {{reason}}',
  },
  decision: {
    title: 'Onay',
    onBoard: '{{role}} gemide onaylar. Ofis kaydı yalnız görür.',
    officeStep: 'Ofiste {{role}} kararı bekleniyor.',
    subject: 'ISM form kaydı {{record}}',
    lateReason: 'Gerekçe (adımın süresi geçti: geç kararı açıklayın)',
  },
  submit: {
    title: '{{record}} gönder',
    body_one:
      'Gönderim, {{record}} kaydını {{count}} dosyasıyla imzalar ve onaya gönderir. Bundan sonraki değişiklikler Amend ile yapılır ve bu revizyon görünür kalır.',
    body_other:
      'Gönderim, {{record}} kaydını {{count}} dosyasıyla imzalar ve onaya gönderir. Bundan sonraki değişiklikler Amend ile yapılır ve bu revizyon görünür kalır.',
  },
  resubmit: {
    title: 'Değişmeden yeniden gönder',
    body: '{{record}} kaydı aynı dosyalar ve revizyonla yeniden onaya gönderilir.',
    reason: 'Değişmeden yeniden gönderme gerekçesi',
  },
  void: {
    title: '{{record}} iptal et',
    body: 'İptal edilen kayıt silinmez: revizyonları ve dosyaları görünür kalır, İPTAL olarak işaretlenir. İptal geri alınmaz; gerekirse yeni kayıt açın.',
    reason: 'İptal gerekçesi',
    note: 'Bu kaydı iptal ediyorsunuz. Adınız, zaman ve gerekçe kayda yazılır.',
  },
  discard: {
    title: '{{record}} taslağından vazgeç',
    body: 'Taslak hiç gönderilmedi. Gerekçenizle "Vazgeçildi" olarak listede kalır; kayıt numarası yeniden kullanılmaz.',
  },
  newRecord: {
    title: 'Yeni kayıt',
    intro:
      '{{form}} için kâğıt formu tarama ya da fotoğrafla kayda alın. Kayıt açılınca kayıt numarası ayrılır; sayfaları kayıt sayfasında ekleyin.',
    paperOf: 'Kâğıt şuradan basıldı',
    current: 'Yürürlükteki revizyon ({{revision}})',
    noCurrent: 'Yürürlükte revizyon yok',
    paper: 'Önceki bir revizyon (basılı kopya)',
    revision: 'Basılı kopyanın revizyonu',
    paperReason: 'Neden önceki bir revizyon kullanıldı',
    paperHint: 'Kayıt, önceki bir revizyonun basılı kopyası olarak işaretlenir.',
    titleHint: 'İsteğe bağlı; ör. tatbikat ya da olayın tarihi.',
    number: 'Kayıt açılınca kayıt numarası ayrılır.',
    create: 'Kaydı aç',
  },
  print: {
    record: 'Kayıt {{record}}',
    void: 'İPTAL',
    amended: 'DEĞİŞTİRİLDİ – kayıt revizyonu {{revision}} / {{revisions}}',
    notApproved: 'ONAYLANMADI',
    enteredBy: 'Giren: {{name}}',
    printed: 'Basım {{at}}, {{by}}',
    hash: 'Hash {{hash}}',
    page: 'Sayfa {{page}} / {{pages}}',
  },
  history: {
    description: 'Bu geminin doldurulmuş ISM formları ve onayları.',
    descriptionOffice:
      'Filonun doldurulmuş ISM formları ve onayları. Kayıtlar gemide, gemi uygulamasında girilir ve senkronla gelir; ofis görür ve yalnız ofis adımlarına karar verir.',
    filters: 'Form kayıtlarını süz',
    status: 'Durum',
    category: 'Kategori',
    form: 'Form',
    vessel: 'Gemi',
    from: 'Başlangıç',
    to: 'Bitiş',
    all: 'Tümü',
    allVessels: 'Bütün gemiler',
    awaitingMe: 'Onayımı bekleyenler',
    upcomingMe: 'Sırası gelecek onaylarım',
    amended: 'Değiştirilenler',
    flagged: 'Eski revizyonla doldurulanlar',
    overdue: 'Yalnız süresi geçenler',
    clear: 'Süzgeçleri temizle',
    loading: 'Form kayıtları yükleniyor…',
    loadError: 'Kayıtlar okunamadı.',
    empty: 'Henüz form kaydı yok',
    emptyHint: 'Form List’ten bir form doldurulunca kayıtlar burada görünür.',
    emptyHintOffice:
      'Bir gemi doldurulmuş formu gönderince kayıtlar burada görünür. Formlar gemide, gemi uygulamasında doldurulur.',
    noMatch: 'Süzgeçlere uyan kayıt yok',
    caption: 'Form kayıtları',
    columns: {
      recordNo: 'Kayıt No',
      recordDay: 'Tarih',
      vessel: 'Gemi',
      form: 'Form',
      title: 'Başlık',
      status: 'Durum',
      awaitingSince: 'Bekleme başlangıcı',
    },
    sortBy: '{{column}} sütununa göre sırala',
    showing: '{{total}} kayıttan {{first}}–{{last}} gösteriliyor',
    pages: 'Form kaydı sayfaları',
    ofForm: 'Bu formun kayıtları',
    noneForForm: 'Bu formun henüz kaydı yok.',
    viewAll_one: 'Kaydı Form History’de gör',
    viewAll_other: 'Bütün {{count}} kaydı Form History’de gör',
  },
  cover: {
    title: 'ISM form kaydı',
    recordNo: 'Kayıt No',
    vessel: 'Gemi',
    form: 'Form',
    revision: 'Form revizyonu',
    fillMethod: 'Doldurma yolu',
    recordDay: 'Kayıt tarihi',
    title_: 'Başlık',
    status: 'Durum',
    enteredBy: 'Giren',
    opened: 'Açılış',
    printed: 'Basım',
    revisions: 'Kayıt revizyonları',
    rev: 'Rev',
    at: 'Zaman',
    by: 'Kişi',
    reason: 'Gerekçe',
    hash: 'Hash',
    firstSubmission: 'İlk gönderim',
    approvals: 'Onaylar',
    noApprovals: 'Henüz karar yok.',
    matrix: 'Matris v{{version}}',
    step: 'Adım',
    decision: 'Karar',
    approver: 'Onaylayan ve rolü',
    shipTime: 'Gemi saati',
    utc: 'UTC',
    files: 'Dosyalar',
    file: 'Dosya',
    source: 'Kaynak',
    sha256: 'SHA-256 (12)',
    size: 'Boyut',
    flags: 'Revizyon işaretleri',
    download: 'Kayıt PDF’ini indir',
    print: 'Kaydı yazdır',
    signatures: 'İmzalar',
    signatureStep: '{{step}}. adım · {{role}}',
    signatureTime: '{{utc}} (gemi saati {{offset}})',
    signatureHash: 'Hash {{hash}}',
    unsigned: 'Karar bekleniyor',
  },
  approvals: {
    title: 'Bekleyen onaylar',
    viewAll: 'Tümünü gör',
    pending_one: '{{count}} kayıt kararınızı bekliyor',
    pending_other: '{{count}} kayıt kararınızı bekliyor',
    overdue_one: '{{count}} süresi geçti',
    overdue_other: '{{count}} süresi geçti',
    none: 'Kararınızı bekleyen kayıt yok.',
    step: '{{step}}. adım / {{steps}}',
    bellPending: '{{record}} onayınızı bekliyor',
    bellOverdue: '{{record}} onayınızı bekliyor, süresi geçti',
    bellNotice: '{{record}} taslağınızın revizyonu artık yürürlükte değil',
    bellReturned: '{{record}} size iade edildi',
    upcoming: 'Sırada',
  },
  returned: {
    title: 'Size iade edilenler',
    by: '{{by}}: {{reason}}',
    noReason: 'Gerekçe yazılmadı',
  },
  steps: {
    title: 'Onay adımları',
    step: '{{step}}. adım',
    awaiting: 'Karar bekleniyor',
    notYet: 'Sırası gelmedi',
    waitingFor: '{{role}} onayı bekleniyor',
    submitted: 'Gönderdi',
    kinds: {
      prepare: 'Hazırlayan',
      review: 'Ön onay',
      master: 'Kaptan onayı',
      office: 'Ofis onayı',
    },
  },
  amend: {
    title: '{{record}} değiştir (revizyon {{revision}})',
    intro:
      'Değişiklik, gerekçeli yeni bir kayıt revizyonudur. Önceki revizyon ve dosyaları görünür kalır; kayıt ilk adımdan yeniden onaya gider.',
    reason: 'Değişikliğin gerekçesi',
    reasonHint: '5–2000 karakter; kaydın çıktısında yazar.',
    submit: 'Değişikliği gönder',
    confirmTitle: 'Değişikliği gönder',
    confirmBody: '{{record}} kaydının değişikliği adınıza imzalanır ve onaya gönderilir.',
    dropsApprovals_one: 'Verilmiş onay ({{count}}) düşer; kayıt 1. adımdan yeniden onaya gider.',
    dropsApprovals_other:
      'Verilmiş onaylar ({{count}}) düşer; kayıt 1. adımdan yeniden onaya gider.',
  },
  errors: {
    GENERIC: 'İşlem tamamlanamadı. Yeniden deneyin.',
    FORBIDDEN: 'Bu işlem için yetkiniz yok.',
    NOT_FOUND: 'Kayıt bulunamadı ya da görme yetkiniz yok.',
    UNAUTHENTICATED: 'Oturumunuz kapandı. Yeniden giriş yapın.',
    VALIDATION_FAILED: 'Bazı değerler geçerli değil.',
    INVALID_CREDENTIALS: 'Parola yanlış.',
    INVALID_PASSWORD: 'Parola yanlış.',
    REAUTHENTICATION_REQUIRED: 'Onaylamak için parolanızı girin.',
    TOO_MANY_ATTEMPTS: 'Çok fazla yanlış parola. Birkaç dakika bekleyip yeniden deneyin.',
    ROW_VERSION_CONFLICT: 'Bu taslağı başkası değiştirdi. Yenileyip yeniden deneyin.',
    REVISION_NOT_CURRENT: 'Formun bu revizyonu yürürlükte değil.',
    REVISION_NOT_PUBLISHED:
      'Bu revizyon gemide hiç yürürlüğe girmedi; basılı kopya için seçilemez.',
    REVISION_NOT_FOUND: 'Form revizyonu bulunamadı.',
    REVISION_MISMATCH: 'Revizyon bu forma ait değil.',
    NOT_A_FORM: 'Kayıt yalnız formlara açılır.',
    VESSEL_MISMATCH: 'Bu form başka bir geminin.',
    DOCUMENT_NOT_VISIBLE: 'Bu form bu gemide yok.',
    FILL_METHOD_UNSUPPORTED: 'Bu doldurma yolu bu formda kullanılamaz.',
    RECORD_BINDING_LOCKED: 'Kayıt açıldığı revizyona bağlı kalır.',
    FILE_NOT_ON_BOARD: 'Form dosyası henüz gemiye inmedi.',
    FILE_TOO_LARGE: 'Dosya çok büyük (dosya başına 50 MB, kayıt revizyonu başına 200 MB).',
    TOO_MANY_FILES: 'Bir kayıt revizyonunda en çok 20 dosya olabilir.',
    NO_FILES: 'Göndermeden önce en az bir dosya ekleyin.',
    EMPTY_FILE: 'Dosya boş.',
    DUPLICATE_FILE: 'Bu dosya kayıtta zaten var.',
    UNSUPPORTED_FILE_TYPE:
      'Bu dosya türü kabul edilmiyor (DOCX, XLSX, PDF, JPG ya da PNG; makrolu dosya reddedilir).',
    FILE_TYPE_MISMATCH: 'Dosyanın içeriği adıyla uyuşmuyor.',
    INVALID_FILE: 'Dosya okunamadı.',
    SHIP_FILE_MISSING: 'Dosya artık gemi deposunda yok. Yeniden ekleyin.',
    HASH_MISMATCH: 'Dosya gönderilirken değişti. Yeniden ekleyin.',
    FILES_UNAVAILABLE: 'Gemi dosya deposu kullanılamıyor.',
    DISK_CRITICAL: 'Gemi sunucusunun diski dolmak üzere.',
    RECORD_SUBMITTED: 'Kayıt gönderildi; değiştirmek için Amend kullanın.',
    RECORD_VOID: 'Kayıt iptal edildi.',
    RECORD_DISCARDED: 'Taslaktan vazgeçildi.',
    RECORD_NOT_SUBMITTED: 'Kayıt henüz gönderilmedi.',
    ALREADY_VOIDED: 'Kayıt zaten iptal edildi.',
    ALREADY_SUBMITTED: 'Kayıt zaten gönderildi.',
    INVALID_RECORD_STATE:
      'Bu kaydın durumu belirlenemiyor. Olduğu gibi gösterilir; işlem yapılamaz.',
    NOT_RETURNED: 'Yalnız iade edilen kayıt değişmeden yeniden gönderilebilir.',
    NOT_PENDING: 'Kayıt artık karar beklemiyor.',
    NO_CHANGE: 'Önceki revizyona göre değişen bir şey yok.',
    REASON_REQUIRED: 'Gerekçe girin (en az 5 karakter).',
    REASON_TOO_LONG: 'Gerekçe çok uzun (en çok 2000 karakter).',
    DISCARD_NOT_ALLOWED: 'Bu taslaktan vazgeçme yetkiniz yok.',
    CONTENT_HASH_MISMATCH: 'Kaydın içeriği değişti. Yenileyip yeniden deneyin.',
    RETURN_REASON_REQUIRED: 'İade gerekçesini girin.',
    LATE_REASON_REQUIRED: 'Adımın süresi geçti: geç kararın açıklamasını yazın.',
    STALE_DECISION: 'Siz bakarken kayıt değişti. Yenileyip yeniden karar verin.',
    CONTENT_CHANGED: 'Kaydın içeriği değişti. Yenileyip yeniden karar verin.',
    NO_ELIGIBLE_APPROVER:
      'Gemide bu kaydı onaylayabilecek kimse yok. Ofisten onay matrisini denetlemesini isteyin.',
    NOT_APPROVER: 'Rolünüz bu adımı onaylamıyor.',
    NOT_A_PREPARER:
      'Bu formu onay zincirinde seçilen rütbeler hazırlar. Gönderimi onlardan biri yapmalı.',
    SELF_APPROVAL_FORBIDDEN: 'Girdiğiniz ya da gönderdiğiniz kaydı onaylayamazsınız.',
    SAME_APPROVER_FORBIDDEN: 'Bu kaydın önceki bir adımını zaten onayladınız.',
    OFFICE_STEP: 'Bu adımın kararı ofiste verilir.',
    NOT_OFFICE_STEP: 'Bu adımın kararı gemide verilir.',
    FILE_NOT_UPLOADED: 'Kayıt dosyaları henüz ofise ulaşmadı.',
    APPROVAL_RULE_MISSING: 'Onay matrisinde ISM formları için kural yok.',
    OPEN_FORM_NOT_OFFICE:
      'Bu form PDF ya da görüntü: Print Form’u ya da doldurulabilir PDF’te Fill in app’i kullanın.',
    NO_ASSOCIATED_APP:
      'Bu bilgisayarda bu dosya türünü açan uygulama yok. Bunun yerine Print Form ya da Fill in app kullanın.',
    FILE_STILL_OPEN: 'Belge hâlâ açık görünüyor. Önce kaydedip kapatın.',
    NO_WORKING_COPY: 'Önce formu Formu doldur ile açın, doldurup kaydedin.',
    UNCHANGED_TEMPLATE: 'Dosya doldurulmamış görünüyor: boş formla aynı.',
    PDF_FAILED: 'Formun PDF kopyası üretilemedi; formun kendisi eklendi.',
    OFFICE_PACK_MISSING: 'Felixmate Office Pack bu bilgisayarda kurulu değil.',
    LOW_RESOLUTION: 'Görüntü A4’te 300 dpi’ın altında; el yazısı zor okunabilir.',
    OPEN_FAILED: 'Dosya bu bilgisayarda açılamadı.',
    READ_FAILED: 'Dosya bu bilgisayardan okunamadı.',
    SHIP_UNREACHABLE: 'Gemi sunucusuna ulaşılamıyor.',
    PDF_HAS_SCRIPTS: 'Bu PDF betik içeriyor; uygulamada doldurulamaz.',
    PDF_IS_XFA: 'Bu PDF XFA formu kullanıyor; uygulamada doldurulamaz.',
    PDF_NO_FIELDS: 'Bu PDF’te form alanı yok.',
    PDF_FORM_CHECK_FAILED:
      'PDF’in form alanlarını denetlemek için dosya bu bilgisayarda açılamadı. Formu yeniden seçin ya da Formu Yazdır veya Yeni kayıt kullanın.',
    PREVIEW_NOT_READY: 'Önizleme hâlâ yükleniyor. Birazdan yeniden deneyin.',
    POPUP_BLOCKED: 'Tarayıcı yeni sekmeyi engelledi. Bu site için açılır pencerelere izin verin.',
    NETWORK: 'Sunucuya ulaşılamıyor.',
  },
};
